import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  GET_ALL_SUBJECTS,
  GET_GISTS_BY_SUBJECT,
  GET_GISTS_BY_USER,
} from "../../services/graphql/queriesMutations";
import Gist from "./Gist";
import { groupGistsByParent } from "../../services/utils/groupGistsByParent";
import { GistType } from "../../services/types";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import DiscussionMap from "../discussion/DiscussionMap";
import "../subject/tree.css";
import {
  getSubjectTreeBrushStyle,
  persistSubjectTreeStyle,
  readStoredSubjectTreeStyle,
  subjectTreeStyleOptions,
  type SubjectTreeNodeStyle,
} from "../subject/subjectTreeStyle";

interface Subject {
  id: number;
  title: string;
  parentId: number | null;
}

const GistSkeleton = () => (
  <div className="w-full space-y-6">
    <div className="flex justify-center">
      <div className="skeleton" style={{ width: "240px", height: "32px" }} />
    </div>
    {[1, 2].map((i) => (
      <div
        key={i}
        className="skeleton-card skeleton"
        style={{ borderRadius: "var(--radius-lg)", height: "120px" }}
      />
    ))}
  </div>
);

type ViewMode = "reader" | "discussion";

const findPathToGist = (nodes: GistType[], targetId: number | null): number[] | null => {
  if (!targetId) return null;

  const walk = (currentNodes: GistType[], currentPath: number[]): number[] | null => {
    for (const node of currentNodes) {
      const nextPath = [...currentPath, node.id];
      if (node.id === targetId) {
        return nextPath;
      }

      if (Array.isArray(node.gists) && node.gists.length > 0) {
        const found = walk(node.gists, nextPath);
        if (found) return found;
      }
    }

    return null;
  };

  return walk(nodes, []);
};

const parseTargetGistId = (value: string | null): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const IndexGist = () => {
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("discussion");
  const [readerTargetPath, setReaderTargetPath] = useState<number[] | null>(null);
  const [nodeStyle, setNodeStyle] = useState<SubjectTreeNodeStyle>(readStoredSubjectTreeStyle);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeTargetGistId = parseTargetGistId(
    searchParams.get("gistId") || searchParams.get("targetGistId"),
  );
  const lastAppliedRouteTargetRef = useRef<string | null>(null);
  const {
    id,
    title: subjectTitle,
    type,
  } = useParams<{
    type?: string;
    title?: string;
    id?: string;
  }>();

  useEffect(() => {
    persistSubjectTreeStyle(nodeStyle);
  }, [nodeStyle]);

  useEffect(() => {
    setReaderTargetPath(null);
    setIsFocusMode(false);
    setViewMode(type === "subject" ? "discussion" : "reader");
  }, [id, type]);

  const query = type === "subject" ? GET_GISTS_BY_SUBJECT : GET_GISTS_BY_USER;
  const variables =
    type === "subject" ? { subjectId: Number(id) } : { userId: Number(id) };

  const decodedRouteTitle = subjectTitle
    ? decodeURIComponent(subjectTitle)
    : type === "subject"
      ? "Subject"
      : "User";

  const { data, loading, error, refetch } = useQuery(query, { variables });
  const { data: subjectData } = useQuery(GET_ALL_SUBJECTS, {
    skip: type !== "subject",
    fetchPolicy: "cache-first",
  });

  const allSubjects = useMemo<Subject[]>(() => subjectData?.subjects ?? [], [subjectData]);
  const currentSubjectId = Number(id) || 0;
  const currentSubject =
    type === "subject"
      ? allSubjects.find((subject) => subject.id === currentSubjectId) ?? null
      : null;

  const subjectChildCount = useMemo(() => {
    if (type !== "subject") return 0;
    return allSubjects.filter((subject) => subject.parentId === currentSubjectId).length;
  }, [allSubjects, currentSubjectId, type]);

  const subjectDepth = useMemo(() => {
    if (type !== "subject" || !currentSubject) return 0;

    const byId = new Map(allSubjects.map((subject) => [subject.id, subject]));
    let depth = 0;
    let parentId = currentSubject.parentId;
    const safety = new Set<number>();

    while (parentId !== null && byId.has(parentId) && !safety.has(parentId)) {
      safety.add(parentId);
      depth += 1;
      parentId = byId.get(parentId)?.parentId ?? null;
    }

    return depth;
  }, [allSubjects, currentSubject, type]);

  const gists = useMemo<GistType[]>(
    () => data?.gistsByUser || data?.gistsBySubject || [],
    [data]
  );
  const groupedGistData = useMemo(
    () => (type === "subject" ? groupGistsByParent(gists) : gists),
    [gists, type],
  );

  useEffect(() => {
    if (!routeTargetGistId || groupedGistData.length === 0) return;

    const targetKey = `${type || ""}:${id || ""}:${routeTargetGistId}`;
    if (lastAppliedRouteTargetRef.current === targetKey) return;

    const path = findPathToGist(groupedGistData, routeTargetGistId);
    if (!path) return;

    lastAppliedRouteTargetRef.current = targetKey;
    setReaderTargetPath(path);
    setViewMode("reader");
    setIsFocusMode(false);
  }, [groupedGistData, id, routeTargetGistId, type]);

  const visibleGistCount = gists.length;

  const subjectDisplayTitle = currentSubject?.title ?? decodedRouteTitle;

  const subjectCardClasses = useMemo(() => {
    if (type !== "subject") return "";

    const normalizedDepth = Math.min(subjectDepth, 2);
    return [
      "subject-tree-node-card",
      "gist-subject-preview-card",
      subjectDepth === 0 ? "subject-tree-node-card-top subject-tree-node-card-focus" : "",
      normalizedDepth === 1 ? "subject-tree-node-card-mid" : "",
      normalizedDepth >= 2 ? "subject-tree-node-card-leaf" : "",
      subjectChildCount > 0
        ? "subject-tree-node-card-has-children"
        : "subject-tree-node-card-terminal",
      `subject-tree-node-depth-${normalizedDepth}`,
    ]
      .filter(Boolean)
      .join(" ");
  }, [subjectChildCount, subjectDepth, type]);

  const subjectCardStyle = useMemo(
    () =>
      type === "subject"
        ? getSubjectTreeBrushStyle(currentSubjectId, subjectDisplayTitle, subjectDepth, nodeStyle)
        : undefined,
    [currentSubjectId, nodeStyle, subjectDepth, subjectDisplayTitle, type],
  );

  const subjectChildLabel =
    subjectChildCount === 0
      ? "No sub-subjects"
      : `${subjectChildCount} sub-subject${subjectChildCount === 1 ? "" : "s"}`;

  const handleOpenFullGist = (path: number[]) => {
    setReaderTargetPath(path);
    setViewMode("reader");
  };

  const handleAfterDeleteGist = async () => {
    await refetch();
  };

  if (loading) {
    return (
      <div className={`gist-page-shell ${isFocusMode ? "is-focus-mode" : ""}`}>
        <GistSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex flex-col h-[60vh] w-full justify-center items-center"
        style={{ fontFamily: "var(--font-primary)" }}
      >
        <h1
          className="text-xl font-semibold mb-2"
          style={{ color: "var(--color-terracotta)" }}
        >
          Something went wrong
        </h1>
        <p style={{ color: "var(--color-gray-warm)" }}>
          Unable to load gists. Please try again.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 py-2 px-6 text-sm font-medium transition-all duration-200"
          style={{
            backgroundColor: "var(--color-forest)",
            color: "white",
            borderRadius: "var(--radius-md)",
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  if (gists.length === 0) {
    return (
      <div
        className="flex flex-col h-[60vh] w-full justify-center items-center"
        style={{ fontFamily: "var(--font-primary)" }}
      >
        <div
          className="text-6xl mb-4 opacity-30"
          style={{ color: "var(--color-forest)" }}
        >
          📝
        </div>
        <h1
          className="text-xl font-semibold mb-2"
          style={{ color: "var(--color-charcoal)" }}
        >
          No Gists Yet
        </h1>
        <p style={{ color: "var(--color-gray-warm)" }}>
          This subject doesn't have any gists to display.
        </p>
        <button
          onClick={() => navigate("/")}
          className="mt-6 py-2 px-6 text-sm font-medium transition-all duration-200"
          style={{
            border: "1px solid var(--color-forest)",
            color: "var(--color-forest)",
            borderRadius: "var(--radius-md)",
            backgroundColor: "transparent",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--color-forest)";
            e.currentTarget.style.color = "white";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "var(--color-forest)";
          }}
        >
          Back to Subjects
        </button>
      </div>
    );
  }

  return (
    <div className={`gist-page-shell ${isFocusMode ? "is-focus-mode" : ""}`}>
      <div className={`gist-page-heading-wrap ${isFocusMode ? "is-focus-mode" : ""}`}>
        <button onClick={() => navigate("/")} className="gist-page-back-link">
          ← Back to Subjects
        </button>

        {type === "subject" ? (
          <div className={`gist-subject-heading subject-tree-style-${nodeStyle}`}>
            <div className="gist-subject-heading-main">
              <div className={subjectCardClasses} style={subjectCardStyle} aria-label={`Subject: ${subjectDisplayTitle}`}>
                <span className="subject-tree-node-brush" aria-hidden="true" />
                <div className="subject-tree-node-main">
                  <div className="subject-tree-node-title">{subjectDisplayTitle}</div>
                  <div className="subject-tree-node-meta">{subjectChildLabel}</div>
                </div>
              </div>

              <div className="subject-tree-style-panel" aria-label="Subject node style">
                <span className="subject-tree-style-label">Node style</span>
                <div className="subject-tree-style-toggle" role="group" aria-label="Choose subject node style">
                  {subjectTreeStyleOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={[
                        "subject-tree-style-option",
                        nodeStyle === option.value ? "subject-tree-style-option-active" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => setNodeStyle(option.value)}
                      aria-pressed={nodeStyle === option.value}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <p className="gist-subject-heading-copy">
              {visibleGistCount} {visibleGistCount === 1 ? "gist" : "gists"} in this subject.
            </p>
          </div>
        ) : (
          <div className="gist-page-title-wrap" aria-label={`User: ${decodedRouteTitle}`}>
            <div
              className="gist-page-subject-kicker"
              style={{
                marginBottom: "6px",
                textAlign: "center",
                color: "var(--color-gray-warm)",
                fontSize: "12px",
                fontWeight: 800,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              User
            </div>
            <h1 className="gist-page-title">{decodedRouteTitle}</h1>
            <p
              className="gist-page-subtitle"
              style={{
                margin: "10px 0 0",
                textAlign: "center",
                color: "var(--gist-shell-muted)",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              Gists by this user
            </p>
          </div>
        )}

        <div className="discussion-view-toggle" role="group" aria-label="View mode">
          <button
            type="button"
            className={`discussion-view-toggle-btn${viewMode === "reader" ? " is-active" : ""}`}
            onClick={() => setViewMode("reader")}
          >
            Reader View
          </button>
          <button
            type="button"
            className={`discussion-view-toggle-btn${viewMode === "discussion" ? " is-active" : ""}`}
            onClick={() => setViewMode("discussion")}
          >
            Discussion View
          </button>
        </div>
      </div>

      {viewMode === "reader" && groupedGistData.length > 0 && (
        <Gist
          gists={groupedGistData}
          onFocusModeChange={setIsFocusMode}
          onAfterDelete={handleAfterDeleteGist}
          targetPath={readerTargetPath}
        />
      )}

      {viewMode === "discussion" && (
        <DiscussionMap gists={groupedGistData} onOpenFullGist={handleOpenFullGist} />
      )}
    </div>
  );
};

export default IndexGist;
